import type { AdminAiCostsResponse, AdminAiCostTotals, AiUsageFeature } from '@pulsify/shared/dto'
import { Cpu } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAdminAiCosts } from '~/entities/stats/model/use-admin-stats'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Card, CardContent, CardHeader, CardTitle } from '~/shared/ui/card'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'

const FEATURE_LABELS: Record<AiUsageFeature, () => string> = {
  event_with_cover: m.aiFeatureEventWithCover,
  event_start: m.aiFeatureEventStart,
  event_cover: m.aiFeatureEventCover,
  event_photo: m.aiFeatureEventPhoto,
  ai_text: m.aiFeatureAiText,
  event_translation: m.aiFeatureEventTranslation,
  cover_image: m.aiFeatureCoverImage,
  format: m.aiFeatureFormat,
  refine: m.aiFeatureRefine,
  menu_scan: m.aiFeatureMenuScan,
  plan_assistant: m.aiFeaturePlanAssistant,
  place_from_website: m.aiFeaturePlaceFromWebsite,
  search_embeddings: m.aiFeatureSearchEmbeddings,
  item_sorting: m.aiFeatureItemSorting,
}

const featureLabel = (feature: string) =>
  feature in FEATURE_LABELS ? FEATURE_LABELS[feature as AiUsageFeature]() : feature

type MonthRow = AdminAiCostsResponse['monthly'][number]

function sumTotals(rows: AdminAiCostTotals[]): AdminAiCostTotals {
  const sum = (pick: (row: AdminAiCostTotals) => number) =>
    rows.reduce((total, row) => total + pick(row), 0)
  const costUsd = sum((row) => row.costUsd)
  const creditsCharged = sum((row) => row.creditsCharged)
  return {
    calls: sum((row) => row.calls),
    failedCalls: sum((row) => row.failedCalls),
    unpricedCalls: sum((row) => row.unpricedCalls),
    inputTokens: sum((row) => row.inputTokens),
    outputTokens: sum((row) => row.outputTokens),
    costUsd,
    creditsCharged,
    usdPerCredit: creditsCharged > 0 ? costUsd / creditsCharged : null,
  }
}

function useFormats() {
  return useMemo(() => {
    const locale = getLocale()
    const count = new Intl.NumberFormat(locale)
    const usd = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' })
    const perCredit = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumSignificantDigits: 3,
    })
    const month = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
    const day = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' })
    return {
      count: (value: number) => count.format(value),
      usd: (value: number) => usd.format(value),
      perCredit: (value: number | null) => (value === null ? '-' : perCredit.format(value)),
      month: (value: string) => month.format(new Date(`${value}-01T12:00:00`)),
      day: (value: string) => day.format(new Date(`${value}T12:00:00`)),
    }
  }, [])
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-muted-foreground text-sm">{label}</p>
        <p className="mt-1 font-semibold text-2xl tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function TotalsCells({ totals }: { totals: AdminAiCostTotals }) {
  const format = useFormats()
  return (
    <>
      <td className="py-3 pr-4 text-right tabular-nums">{format.count(totals.calls)}</td>
      <td className="py-3 pr-4 text-right tabular-nums">{format.count(totals.failedCalls)}</td>
      <td className="py-3 pr-4 text-right text-muted-foreground tabular-nums whitespace-nowrap">
        {format.count(totals.inputTokens)} / {format.count(totals.outputTokens)}
      </td>
      <td className="py-3 pr-4 text-right tabular-nums whitespace-nowrap">
        {format.usd(totals.costUsd)}
        {totals.unpricedCalls > 0 ? (
          <span className="block text-muted-foreground text-xs">
            {m.aiCostsUnpriced({ count: format.count(totals.unpricedCalls) })}
          </span>
        ) : null}
      </td>
      <td className="py-3 pr-4 text-right tabular-nums">{format.count(totals.creditsCharged)}</td>
      <td className="py-3 pr-4 text-right tabular-nums">{format.perCredit(totals.usdPerCredit)}</td>
    </>
  )
}

function TotalsHeadings({ first }: { first: string }) {
  return (
    <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
      <th className="py-3 pr-4 pl-4 font-medium">{first}</th>
      <th className="py-3 pr-4 font-medium text-right">{m.aiCostsColumnCalls()}</th>
      <th className="py-3 pr-4 font-medium text-right">{m.aiCostsColumnFailed()}</th>
      <th className="py-3 pr-4 font-medium text-right">{m.aiCostsColumnTokens()}</th>
      <th className="py-3 pr-4 font-medium text-right">{m.aiCostsColumnCost()}</th>
      <th className="py-3 pr-4 font-medium text-right">{m.aiCostsColumnCredits()}</th>
      <th className="py-3 pr-4 font-medium text-right">{m.aiCostsColumnPerCredit()}</th>
    </tr>
  )
}

function MonthTable({ rows }: { rows: MonthRow[] }) {
  const total = sumTotals(rows)
  return (
    <table className="w-full text-sm">
      <thead>
        <TotalsHeadings first={m.aiCostsColumnFeature()} />
      </thead>
      <tbody className="[&_td:first-child]:pl-4">
        {rows.map((row) => (
          <tr key={row.feature} className="border-border border-b">
            <td className="py-3 pr-4 font-medium">{featureLabel(row.feature)}</td>
            <TotalsCells totals={row} />
          </tr>
        ))}
        <tr className="font-semibold">
          <td className="py-3 pr-4">{m.aiCostsTotal()}</td>
          <TotalsCells totals={total} />
        </tr>
      </tbody>
    </table>
  )
}

function DailyBars({ daily }: { daily: AdminAiCostsResponse['daily'] }) {
  const format = useFormats()
  const highest = Math.max(...daily.map((entry) => entry.costUsd), 0)
  return (
    <ul className="space-y-1.5">
      {daily.map((entry) => (
        <li key={entry.day} className="flex items-center gap-3 text-sm">
          <span className="w-14 text-muted-foreground tabular-nums shrink-0">
            {format.day(entry.day)}
          </span>
          <span className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
            <span
              className="block bg-brand rounded-full h-full"
              style={{ width: highest > 0 ? `${(entry.costUsd / highest) * 100}%` : 0 }}
            />
          </span>
          <span className="w-20 text-right tabular-nums shrink-0">{format.usd(entry.costUsd)}</span>
          <span className="hidden sm:block w-24 text-muted-foreground text-right tabular-nums shrink-0">
            {m.aiCostsCalls({ count: format.count(entry.calls) })}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function AiCostsPage() {
  const query = useAdminAiCosts()
  const format = useFormats()
  const months = useMemo(
    () => [...new Set((query.data?.monthly ?? []).map((row) => row.month))],
    [query.data],
  )
  const [picked, setPicked] = useState<string | null>(null)
  const month = picked && months.includes(picked) ? picked : (months[0] ?? null)
  const monthRows = useMemo(
    () => (query.data?.monthly ?? []).filter((row) => row.month === month),
    [query.data, month],
  )
  const monthTotal = sumTotals(monthRows)

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-16">
      <div className="space-y-8 mx-auto max-w-6xl">
        <header className="flex flex-wrap justify-between items-end gap-x-6 gap-y-4">
          <div className="space-y-1.5">
            <h1 className="flex items-center gap-2.5 font-semibold text-foreground text-3xl tracking-tight">
              <Cpu className="w-7 h-7" aria-hidden />
              {m.aiCostsTitle()}
            </h1>
            <p className="text-muted-foreground text-sm">{m.aiCostsSubtitle()}</p>
          </div>
          {months.length > 1 ? (
            <SegmentedControl
              label={m.aiCostsMonthLabel()}
              value={month ?? ''}
              onChange={setPicked}
              options={months.map((value) => ({ value, label: format.month(value) }))}
            />
          ) : null}
        </header>

        <div className="gap-4 grid sm:grid-cols-3">
          <SummaryTile
            label={m.aiCostsMonthCost()}
            value={query.data ? format.usd(monthTotal.costUsd) : '-'}
          />
          <SummaryTile
            label={m.aiCostsMonthCredits()}
            value={query.data ? format.count(monthTotal.creditsCharged) : '-'}
          />
          <SummaryTile
            label={m.aiCostsPerCredit()}
            value={query.data ? format.perCredit(monthTotal.usdPerCredit) : '-'}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{month ? format.month(month) : m.aiCostsTitle()}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {query.isLoading ? (
              <div className="space-y-3 p-6">
                {['a', 'b', 'c', 'd'].map((placeholder) => (
                  <Skeleton key={placeholder} className="w-full h-10" />
                ))}
              </div>
            ) : monthRows.length === 0 ? (
              <p className="p-12 text-muted-foreground text-sm text-center">{m.aiCostsEmpty()}</p>
            ) : (
              <MonthTable rows={monthRows} />
            )}
          </CardContent>
        </Card>
        <p className="text-muted-foreground text-xs">{m.aiCostsListPriceNote()}</p>

        <div className="gap-6 grid lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{m.aiCostsDailyTitle()}</CardTitle>
            </CardHeader>
            <CardContent>
              {query.data ? (
                <DailyBars daily={query.data.daily} />
              ) : (
                <Skeleton className="w-full h-64" />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{m.aiCostsTopTitle()}</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {query.data ? (
                query.data.topBusinesses.length === 0 ? (
                  <p className="p-12 text-muted-foreground text-sm text-center">
                    {m.aiCostsEmpty()}
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                        <th className="py-3 pr-4 pl-4 font-medium">{m.aiCostsColumnBusiness()}</th>
                        <th className="py-3 pr-4 font-medium text-right">
                          {m.aiCostsColumnCalls()}
                        </th>
                        <th className="py-3 pr-4 font-medium text-right">
                          {m.aiCostsColumnCost()}
                        </th>
                        <th className="py-3 pr-4 font-medium text-right">
                          {m.aiCostsColumnCredits()}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {query.data.topBusinesses.map((entry) => (
                        <tr key={entry.businessId} className="border-border border-b last:border-0">
                          <td className="py-3 pr-4 pl-4 font-medium">{entry.name}</td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {format.count(entry.calls)}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {format.usd(entry.costUsd)}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {format.count(entry.creditsCharged)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : (
                <Skeleton className="m-6 h-64" />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
